import { BadgeLegacy } from '@accure/ui';
import { format } from 'date-fns/fp';
import { ArrowDown, ArrowUp, User } from 'lucide-react';

import type { JobDetails } from '@/api/fe-api/domain/entities/capacity-evaluation';

import { getStatusColor } from '../get-status-color';

export type CapacityTestMetadataProps = {
  data: JobDetails;
};

const formatDatetime = format("yyyy-MM-dd 'at' HH:mm zzz");

export function CapacityTestMetadata({ data }: CapacityTestMetadataProps) {
  return (
    <div>
      <div className="mb-8 space-y-2">
        <div className="flex items-center">
          <h2 className="text-xl font-semibold">
            Battery Capacity Test Details
          </h2>
          <BadgeLegacy color={getStatusColor(data.status)} className="ml-4">
            {data.status}
          </BadgeLegacy>
        </div>
        <div className="mt-1 text-sm text-gray-500">Test ID: {data.id}</div>
      </div>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-x-12 gap-y-6 md:grid-cols-2">
          <div>
            <div className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-500">
              <ArrowUp className="size-4" />
              <span>Charging Session</span>
            </div>

            <div className="space-y-4 pl-1">
              <div>
                <p className="mb-1 text-sm text-gray-500">Start:</p>
                <p className="font-medium">
                  {formatDatetime(data.chargeStartTime)}
                </p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-500">End:</p>
                <p className="font-medium">
                  {formatDatetime(data.chargeEndTime)}
                </p>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-500">
              <ArrowDown className="size-4" />
              <span>Discharging Session</span>
            </div>

            <div className="space-y-4 pl-1">
              <div>
                <p className="mb-1 text-sm text-gray-500">Start:</p>
                <p className="font-medium">
                  {formatDatetime(data.dischargeStartTime)}
                </p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-500">End:</p>
                <p className="font-medium">
                  {formatDatetime(data.dischargeEndTime)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div role="separator" className="border-t border-gray-200" />
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-gray-500">
          <div className="flex items-center gap-1.5">
            <User className="size-3" />
            <span>Created by:</span>
            <span className="text-gray-500/80">{data.createdBy}</span>
          </div>
          <div>
            <span>Created at:</span>
            <span className="ml-1.5 text-gray-500/80">
              {formatDatetime(data.createdAt)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
